'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RealEstateCalculator() {
  const t = useTranslations("tools.realEstateCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentTab, setCurrentTab] = useState<'rental' | 'flip' | 'mortgage'>('rental');
  const [showResults, setShowResults] = useState(false);

  // Rental Property Inputs
  const [rentalInputs, setRentalInputs] = useState({
    purchasePrice: 300000,
    downPayment: 60000,
    interestRate: 6.5,
    loanTerm: 30,
    monthlyRent: 2500,
    propertyTax: 3600,
    insurance: 1200,
    hoaFees: 0,
    maintenance: 3000,
    vacancyRate: 5,
    closingCosts: 9000,
    appreciation: 3,
  });

  const [rentalResults, setRentalResults] = useState<any>(null);

  // Flip Inputs
  const [flipInputs, setFlipInputs] = useState({
    purchasePrice: 200000,
    repairCosts: 50000,
    arv: 300000,
    holdingCosts: 2000,
    holdingPeriod: 6,
    sellingCosts: 8,
  });

  const [flipResults, setFlipResults] = useState<any>(null);

  // Mortgage Inputs
  const [mortgageInputs, setMortgageInputs] = useState({
    homePrice: 350000,
    downPayment: 70000,
    interestRate: 6.5,
    loanTerm: 30,
    propertyTax: 4200,
    insurance: 1400,
    pmi: 0,
    hoaFees: 0,
  });

  const [mortgageResults, setMortgageResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (value: number): string => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formatted = '$' + absValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return isNegative ? '-' + formatted : formatted;
  };

  const calculateRental = (e: React.FormEvent) => {
    e.preventDefault();

    const loanAmount = rentalInputs.purchasePrice - rentalInputs.downPayment;
    const monthlyRate = rentalInputs.interestRate / 100 / 12;
    const numPayments = rentalInputs.loanTerm * 12;

    const monthlyMortgage = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);

    const effectiveRent = rentalInputs.monthlyRent * (1 - rentalInputs.vacancyRate / 100);
    const monthlyPropertyTax = rentalInputs.propertyTax / 12;
    const monthlyInsurance = rentalInputs.insurance / 12;
    const monthlyMaintenance = rentalInputs.maintenance / 12;

    const totalMonthlyExpenses = monthlyMortgage + monthlyPropertyTax + monthlyInsurance + rentalInputs.hoaFees + monthlyMaintenance;
    const monthlyCashFlow = effectiveRent - totalMonthlyExpenses;
    const annualCashFlow = monthlyCashFlow * 12;

    const totalCashInvested = rentalInputs.downPayment + rentalInputs.closingCosts;
    const cocReturn = (annualCashFlow / totalCashInvested) * 100;

    const annualNOI = (effectiveRent * 12) - (rentalInputs.propertyTax + rentalInputs.insurance + rentalInputs.maintenance + (rentalInputs.hoaFees * 12));
    const capRate = (annualNOI / rentalInputs.purchasePrice) * 100;

    const appreciationGain = rentalInputs.purchasePrice * (rentalInputs.appreciation / 100);
    const totalReturn = ((annualCashFlow + appreciationGain) / totalCashInvested) * 100;

    setRentalResults({
      monthlyCashFlow,
      cocReturn,
      capRate,
      totalReturn,
      monthlyMortgage,
      effectiveRent,
      monthlyPropertyTax,
      monthlyInsurance,
      monthlyMaintenance,
      totalMonthlyExpenses,
      annualCashFlow,
      totalCashInvested,
    });

    setShowResults(true);
  };

  const calculateFlip = (e: React.FormEvent) => {
    e.preventDefault();

    const totalHoldingCosts = flipInputs.holdingCosts * flipInputs.holdingPeriod;
    const sellingCosts = flipInputs.arv * (flipInputs.sellingCosts / 100);
    const totalInvestment = flipInputs.purchasePrice + flipInputs.repairCosts + totalHoldingCosts;
    const totalProfit = flipInputs.arv - totalInvestment - sellingCosts;
    const roi = (totalProfit / totalInvestment) * 100;

    const rule70Max = (flipInputs.arv * 0.7) - flipInputs.repairCosts;
    const rule70Status = flipInputs.purchasePrice <= rule70Max;

    setFlipResults({
      totalProfit,
      roi,
      rule70Status,
      rule70Max,
      totalInvestment,
      totalHoldingCosts,
      sellingCosts,
    });

    setShowResults(true);
  };

  const calculateMortgage = (e: React.FormEvent) => {
    e.preventDefault();

    const loanAmount = mortgageInputs.homePrice - mortgageInputs.downPayment;
    const monthlyRate = mortgageInputs.interestRate / 100 / 12;
    const numPayments = mortgageInputs.loanTerm * 12;

    const principalInterest = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);

    const monthlyPropertyTax = mortgageInputs.propertyTax / 12;
    const monthlyInsurance = mortgageInputs.insurance / 12;

    const totalMonthlyPayment = principalInterest + monthlyPropertyTax + monthlyInsurance + mortgageInputs.pmi + mortgageInputs.hoaFees;

    const totalPaid = (principalInterest * numPayments) + (monthlyPropertyTax * numPayments) + (monthlyInsurance * numPayments) + (mortgageInputs.pmi * numPayments) + (mortgageInputs.hoaFees * numPayments);
    const totalInterest = (principalInterest * numPayments) - loanAmount;

    setMortgageResults({
      totalMonthlyPayment,
      totalInterest,
      totalPaid,
      loanAmount,
      principalInterest,
      monthlyPropertyTax,
      monthlyInsurance,
    });

    setShowResults(true);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
    {},
    { gap: '1rem', marginBottom: '2rem' },
    {}
  );

  const tabButtonStyle = (isActive: boolean) => getResponsiveStyle(
    {
      background: isActive ? '#2563eb' : 'white',
      color: isActive ? 'white' : '#475569',
      border: `2px solid ${isActive ? '#2563eb' : '#e2e8f0'}`,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
    },
    { padding: '0.625rem 1rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' },
    {}
  );

  const formStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    {}
  );

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' },
    { gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }
  );

  const formGroupStyle = { display: 'flex', flexDirection: 'column' as const };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    {}
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1.0625rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      fontWeight: 700,
      color: '#fff',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem', borderRadius: '0.75rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    {}
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem', marginBottom: '2rem' },
    { gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }
  );

  const resultCardStyle = (highlight: boolean = false) => getResponsiveStyle(
    {
      background: highlight ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderLeft: `4px solid ${highlight ? '#f59e0b' : '#2563eb'}`,
      borderRadius: '0.75rem',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 },
    { fontSize: '0.8125rem' },
    { fontSize: '0.875rem' },
    {}
  );

  const resultValueStyle = (highlight: boolean = false) => getResponsiveStyle(
    { display: 'block', color: highlight ? '#92400e' : '#1e293b', fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '1.5rem',
  };

  const thTdStyle = getResponsiveStyle(
    { padding: '0.75rem', textAlign: 'left' as const, borderBottom: '1px solid #e2e8f0' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' }
  );

  const thStyle = { ...thTdStyle, background: '#f8fafc', fontWeight: 600, color: '#334155' };

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #3b82f6',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.9375rem' },
    {}
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem', marginBottom: '2rem' },
    { padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={{ ...summaryTextStyle, marginTop: '0.5rem' }}>{t("toolSubtitle")}</p>
          </div>

          {/* Tabs */}
          <div style={tabsContainerStyle}>
            <button
              style={tabButtonStyle(currentTab === 'rental')}
              onClick={() => { setCurrentTab('rental'); setShowResults(false); }}
            >
              {t("tabRental")}
            </button>
            <button
              style={tabButtonStyle(currentTab === 'flip')}
              onClick={() => { setCurrentTab('flip'); setShowResults(false); }}
            >
              {t("tabFlip")}
            </button>
            <button
              style={tabButtonStyle(currentTab === 'mortgage')}
              onClick={() => { setCurrentTab('mortgage'); setShowResults(false); }}
            >
              {t("tabMortgage")}
            </button>
          </div>

          {/* Rental Property Form */}
          {currentTab === 'rental' && (
            <form style={formStyle} onSubmit={calculateRental}>
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("purchasePrice")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.purchasePrice}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, purchasePrice: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("downPayment")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.downPayment}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, downPayment: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("interestRate")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.interestRate}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, interestRate: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("loanTerm")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.loanTerm}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, loanTerm: parseInt(e.target.value) || 0 })}
                    required
                    min="1"
                    max="50"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("monthlyRent")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.monthlyRent}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, monthlyRent: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="50"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("propertyTax")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.propertyTax}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, propertyTax: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("insurance")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.insurance}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, insurance: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="50"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("hoaFees")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.hoaFees}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, hoaFees: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="10"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("maintenance")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.maintenance}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, maintenance: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("vacancyRate")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.vacancyRate}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, vacancyRate: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    max="100"
                    step="1"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("closingCosts")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.closingCosts}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, closingCosts: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="500"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("appreciation")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={rentalInputs.appreciation}
                    onChange={(e) => setRentalInputs({ ...rentalInputs, appreciation: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                </div>
              </div>

              <button type="submit" style={calculateButtonStyle}>
                {t("calculateButton")}
              </button>
            </form>
          )}

          {/* Flip Form */}
          {currentTab === 'flip' && (
            <form style={formStyle} onSubmit={calculateFlip}>
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("flipPurchasePrice")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={flipInputs.purchasePrice}
                    onChange={(e) => setFlipInputs({ ...flipInputs, purchasePrice: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("repairCosts")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={flipInputs.repairCosts}
                    onChange={(e) => setFlipInputs({ ...flipInputs, repairCosts: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("arv")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={flipInputs.arv}
                    onChange={(e) => setFlipInputs({ ...flipInputs, arv: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("holdingCosts")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={flipInputs.holdingCosts}
                    onChange={(e) => setFlipInputs({ ...flipInputs, holdingCosts: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("holdingPeriod")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={flipInputs.holdingPeriod}
                    onChange={(e) => setFlipInputs({ ...flipInputs, holdingPeriod: parseInt(e.target.value) || 0 })}
                    required
                    min="1"
                    max="60"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("sellingCosts")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={flipInputs.sellingCosts}
                    onChange={(e) => setFlipInputs({ ...flipInputs, sellingCosts: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    max="100"
                    step="0.5"
                  />
                </div>
              </div>

              <button type="submit" style={calculateButtonStyle}>
                {t("calculateButton")}
              </button>
            </form>
          )}

          {/* Mortgage Form */}
          {currentTab === 'mortgage' && (
            <form style={formStyle} onSubmit={calculateMortgage}>
              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("homePrice")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.homePrice}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, homePrice: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("mortDownPayment")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.downPayment}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, downPayment: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="1000"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("mortInterestRate")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.interestRate}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, interestRate: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("mortLoanTerm")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.loanTerm}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, loanTerm: parseInt(e.target.value) || 0 })}
                    required
                    min="1"
                    max="50"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("mortPropertyTax")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.propertyTax}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, propertyTax: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("mortInsurance")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.insurance}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, insurance: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="50"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("pmi")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.pmi}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, pmi: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="10"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("mortHoaFees")}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={mortgageInputs.hoaFees}
                    onChange={(e) => setMortgageInputs({ ...mortgageInputs, hoaFees: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="10"
                  />
                </div>
              </div>

              <button type="submit" style={calculateButtonStyle}>
                {t("calculateButton")}
              </button>
            </form>
          )}

          {/* Results */}
          {showResults && (
            <div style={resultsStyle}>
              {currentTab === 'rental' && rentalResults && (
                <>
                  <h3 style={{ ...h2Style, textAlign: 'center', marginBottom: '1.5rem' }}>{t("rentalResultsTitle")}</h3>

                  <div style={resultsGridStyle}>
                    <div style={resultCardStyle(true)}>
                      <span style={resultLabelStyle}>{t("monthlyCashFlow")}</span>
                      <span style={resultValueStyle(true)}>{formatCurrency(rentalResults.monthlyCashFlow)}</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("cocReturn")}</span>
                      <span style={resultValueStyle()}>{rentalResults.cocReturn.toFixed(2)}%</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("capRate")}</span>
                      <span style={resultValueStyle()}>{rentalResults.capRate.toFixed(2)}%</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("totalRoi")}</span>
                      <span style={resultValueStyle()}>{rentalResults.totalReturn.toFixed(2)}%</span>
                    </div>
                  </div>

                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("incomeExpenses")}</th>
                        <th style={thStyle}>{t("monthly")}</th>
                        <th style={thStyle}>{t("annual")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={thTdStyle}><strong>{t("grossRentalIncome")}</strong></td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.monthlyRent)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.monthlyRent * 12)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("vacancyLoss")} ({rentalInputs.vacancyRate.toFixed(1)}%)</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.monthlyRent * rentalInputs.vacancyRate / 100)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.monthlyRent * rentalInputs.vacancyRate / 100 * 12)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}><strong>{t("effectiveIncome")}</strong></td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.effectiveRent)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.effectiveRent * 12)}</td>
                      </tr>
                      <tr><td style={thTdStyle}>&nbsp;</td><td style={thTdStyle}></td><td style={thTdStyle}></td></tr>
                      <tr>
                        <td style={thTdStyle}>{t("mortgagePayment")}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.monthlyMortgage)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.monthlyMortgage * 12)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("propertyTaxLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.monthlyPropertyTax)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.propertyTax)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("insuranceLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.monthlyInsurance)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.insurance)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("hoaFeesLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.hoaFees)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.hoaFees * 12)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("maintenanceLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.monthlyMaintenance)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalInputs.maintenance)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}><strong>{t("totalExpenses")}</strong></td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.totalMonthlyExpenses)}</td>
                        <td style={thTdStyle}>{formatCurrency(rentalResults.totalMonthlyExpenses * 12)}</td>
                      </tr>
                      <tr><td style={thTdStyle}>&nbsp;</td><td style={thTdStyle}></td><td style={thTdStyle}></td></tr>
                      <tr style={{ background: '#f0fdf4' }}>
                        <td style={thTdStyle}><strong>{t("netCashFlow")}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(rentalResults.monthlyCashFlow)}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(rentalResults.annualCashFlow)}</strong></td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={infoBoxStyle}>
                    <p style={{ margin: 0, color: '#1e40af' }}>
                      <strong>💡 {t("investmentSummary")}</strong> {t("totalCashInvested")}: {formatCurrency(rentalResults.totalCashInvested)}. {rentalResults.monthlyCashFlow > 0 ? t("positiveCashFlowMsg").replace('{cashFlow}', formatCurrency(rentalResults.monthlyCashFlow)).replace('{cocReturn}', rentalResults.cocReturn.toFixed(2)) : t("negativeCashFlowMsg")}
                    </p>
                  </div>
                </>
              )}

              {currentTab === 'flip' && flipResults && (
                <>
                  <h3 style={{ ...h2Style, textAlign: 'center', marginBottom: '1.5rem' }}>{t("flipResultsTitle")}</h3>

                  <div style={resultsGridStyle}>
                    <div style={resultCardStyle(true)}>
                      <span style={resultLabelStyle}>{t("totalProfit")}</span>
                      <span style={resultValueStyle(true)}>{formatCurrency(flipResults.totalProfit)}</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("roi")}</span>
                      <span style={resultValueStyle()}>{flipResults.roi.toFixed(2)}%</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("rule70Check")}</span>
                      <span style={resultValueStyle()}>{flipResults.rule70Status ? '✓ ' + t("pass") : '✗ ' + t("fail")}</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("totalInvestment")}</span>
                      <span style={resultValueStyle()}>{formatCurrency(flipResults.totalInvestment)}</span>
                    </div>
                  </div>

                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("item")}</th>
                        <th style={thStyle}>{t("amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={thTdStyle}>{t("purchasePriceLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(flipInputs.purchasePrice)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("repairCostsLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(flipInputs.repairCosts)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("holdingCostsLabel")} ({flipInputs.holdingPeriod} {t("months")})</td>
                        <td style={thTdStyle}>{formatCurrency(flipResults.totalHoldingCosts)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}><strong>{t("totalInvestmentLabel")}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(flipResults.totalInvestment)}</strong></td>
                      </tr>
                      <tr><td style={thTdStyle}>&nbsp;</td><td style={thTdStyle}></td></tr>
                      <tr>
                        <td style={thTdStyle}>{t("arvLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(flipInputs.arv)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("sellingCostsLabel")} ({flipInputs.sellingCosts.toFixed(1)}%)</td>
                        <td style={thTdStyle}>{formatCurrency(flipResults.sellingCosts)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}><strong>{t("netProceeds")}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(flipInputs.arv - flipResults.sellingCosts)}</strong></td>
                      </tr>
                      <tr><td style={thTdStyle}>&nbsp;</td><td style={thTdStyle}></td></tr>
                      <tr style={{ background: flipResults.totalProfit > 0 ? '#f0fdf4' : '#fef2f2' }}>
                        <td style={thTdStyle}><strong>{t("totalProfitLabel")}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(flipResults.totalProfit)}</strong></td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={infoBoxStyle}>
                    <p style={{ margin: 0, color: '#1e40af' }}>
                      <strong>💡 {t("flipAnalysis")}</strong> {t("rule70Msg").replace('{maxPrice}', formatCurrency(flipResults.rule70Max)).replace('{actualPrice}', formatCurrency(flipInputs.purchasePrice))}. {flipResults.totalProfit > 0 ? t("flipProfitMsg").replace('{profit}', formatCurrency(flipResults.totalProfit)).replace('{roi}', flipResults.roi.toFixed(2)).replace('{months}', flipInputs.holdingPeriod.toString()) : t("flipLossMsg")}
                    </p>
                  </div>
                </>
              )}

              {currentTab === 'mortgage' && mortgageResults && (
                <>
                  <h3 style={{ ...h2Style, textAlign: 'center', marginBottom: '1.5rem' }}>{t("mortgageResultsTitle")}</h3>

                  <div style={resultsGridStyle}>
                    <div style={resultCardStyle(true)}>
                      <span style={resultLabelStyle}>{t("monthlyPayment")}</span>
                      <span style={resultValueStyle(true)}>{formatCurrency(mortgageResults.totalMonthlyPayment)}</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("totalInterest")}</span>
                      <span style={resultValueStyle()}>{formatCurrency(mortgageResults.totalInterest)}</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("totalPaid")}</span>
                      <span style={resultValueStyle()}>{formatCurrency(mortgageResults.totalPaid)}</span>
                    </div>

                    <div style={resultCardStyle()}>
                      <span style={resultLabelStyle}>{t("loanAmount")}</span>
                      <span style={resultValueStyle()}>{formatCurrency(mortgageResults.loanAmount)}</span>
                    </div>
                  </div>

                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t("paymentComponent")}</th>
                        <th style={thStyle}>{t("monthly")}</th>
                        <th style={thStyle}>{t("annual")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={thTdStyle}>{t("principalInterest")}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageResults.principalInterest)}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageResults.principalInterest * 12)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("propertyTaxLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageResults.monthlyPropertyTax)}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageInputs.propertyTax)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("homeownersInsurance")}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageResults.monthlyInsurance)}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageInputs.insurance)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("pmiLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageInputs.pmi)}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageInputs.pmi * 12)}</td>
                      </tr>
                      <tr>
                        <td style={thTdStyle}>{t("hoaFeesLabel")}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageInputs.hoaFees)}</td>
                        <td style={thTdStyle}>{formatCurrency(mortgageInputs.hoaFees * 12)}</td>
                      </tr>
                      <tr style={{ background: '#f0fdf4' }}>
                        <td style={thTdStyle}><strong>{t("totalMonthlyPaymentLabel")}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(mortgageResults.totalMonthlyPayment)}</strong></td>
                        <td style={thTdStyle}><strong>{formatCurrency(mortgageResults.totalMonthlyPayment * 12)}</strong></td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={infoBoxStyle}>
                    <p style={{ margin: 0, color: '#1e40af' }}>
                      <strong>💡 {t("mortgageSummary")}</strong> {((mortgageInputs.downPayment / mortgageInputs.homePrice) * 100).toFixed(1)}% {t("downPaymentMsg")} ({formatCurrency(mortgageInputs.downPayment)}). {t("interestMsg").replace('{years}', mortgageInputs.loanTerm.toString()).replace('{interest}', formatCurrency(mortgageResults.totalInterest)).replace('{loan}', formatCurrency(mortgageResults.loanAmount)).replace('{rate}', (mortgageInputs.interestRate).toFixed(2))}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsPara1")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t("insightMetric1")}</strong> {t("insightMetric1Desc")}</li>
          <li style={listItemStyle}><strong>{t("insightMetric2")}</strong> {t("insightMetric2Desc")}</li>
          <li style={listItemStyle}><strong>{t("insightMetric3")}</strong> {t("insightMetric3Desc")}</li>
          <li style={listItemStyle}><strong>{t("insightMetric4")}</strong> {t("insightMetric4Desc")}</li>
        </ul>
        <p style={paragraphStyle}>{t("insightsPara2")}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t("practice1Title")}</strong> {t("practice1Desc")}</li>
          <li style={listItemStyle}><strong>{t("practice2Title")}</strong> {t("practice2Desc")}</li>
          <li style={listItemStyle}><strong>{t("practice3Title")}</strong> {t("practice3Desc")}</li>
          <li style={listItemStyle}><strong>{t("practice4Title")}</strong> {t("practice4Desc")}</li>
          <li style={listItemStyle}><strong>{t("practice5Title")}</strong> {t("practice5Desc")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>{t("faq5Question")}</h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.thelandgeek.com/blog-real-estate-investing-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("ref1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.obieinsurance.com/blog/best-real-estate-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("ref2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://edinhart.com/rental-property-roi-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("ref3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.fortunebuilders.com/p/real-estate-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("ref4")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}
